import { EmailAuthIcon, GoogleAuthIcon, MobileAuthIcon, UiUtils, type SerializerPhone } from '../index'
import { ApiStatus, UCenterServices } from 'network'
import { CommonConfirmSecurityAuthType } from 'network/src/constants/security'
import { useUserInfoStore } from 'store'
import { useI18n } from 'i18n-utils'

export enum AuthSource {
  /** 通行密钥 */
  PassKey = 'passkey',
  /** 谷歌 */
  Google = 'google',
  /** 手机 */
  Mobile = 'mobile',
  /** 邮箱 */
  Email = 'email',
}

export enum PasswordAuthSource {
  /** 登录密码 */
  Login = 'login',
  /** 支付密码 */
  Trade = 'trade',
}

export enum WithdrawAuthSource {
  /** 提币 */
  Withdraw = 'withdraw',
}

export enum LoginAuthSource {
  /** 登录 */
  Login = 'login',
  /** 忘记密码 */
  ForgetPassword = 'forget-password',
}

export type NeedSecurityAuth = {
  type: AuthSource
  /** 是否完成 */
  isCompleted: boolean
}

/** 安全验证暗要求 */
export const useSecurityAuthStore = () => {
  const userInfoStore = useUserInfoStore()
  const needSecurityAuthMap = ref<Map<AuthSource, NeedSecurityAuth>>(new Map())
  const tempToken = ref('')
  const checkTradePwdVisible = ref(false)
  const showSecurityDialog = ref(false) //ref(!storageDB.get(StorageIndexDBEnum.AgreeNotice))
  const { t } = useI18n()
  const currentAuthSource = ref<AuthSource | PasswordAuthSource | WithdrawAuthSource | LoginAuthSource>()

  const googleCode = ref('')
  const emailCode = ref('')
  const mobileCode = ref('')
  // const appInfoStore = useAppInfoStore()

  const loadingAuthGoogle = ref(false)
  const loadingAuthEmail = ref(false)
  const loadingAuthMobile = ref(false)
  const type = ref<CommonConfirmSecurityAuthType>()

  /** 当前验证选项成功得到的md5字符串，需要在最后的时候提交给后端 */
  const tokenVerify = ref('')

  const mobileCountDown = ref(-1)
  const emailCountDown = ref(-1)

  const isAllAuthCompleted = ref(false)

  const lastVerifyResult = ref<ApiResponse.SafetyAuthResponse>()

  const minVerifyNum = computed(() => {
    return userInfoStore.userInfo?.authInfo.minVerifyNum || 1
  })

  watchEffect(() => {
    if (!userInfoStore.userInfo) return
    const isBindGoogle = userInfoStore.isBindGoogle
    const isBindEmail = userInfoStore.isBindEmail
    const isBindMobile = userInfoStore.isBindMobile
    // TODO: 通行密钥要等一下后端接口，目前后端暂无人力支持
    // const isOpenPassKey = userInfoStore.isOpenPassKey
    // if (appInfoStore.isSupportPassKey && needSecurityAuthMap.value.size < minVerifyNum && isOpenPassKey) {
    //   needSecurityAuthMap.value.set(AuthSource.PassKey, {
    //     type: AuthSource.PassKey,
    //     isCompleted: false,
    //   })
    // }

    if (isBindGoogle) {
      needSecurityAuthMap.value.set(AuthSource.Google, {
        type: AuthSource.Google,
        isCompleted: false,
      })
    }
    if (isBindEmail) {
      needSecurityAuthMap.value.set(AuthSource.Email, {
        type: AuthSource.Email,
        isCompleted: false,
      })
    }
    if (isBindMobile) {
      needSecurityAuthMap.value.set(AuthSource.Mobile, {
        type: AuthSource.Mobile,
        isCompleted: false,
      })
    }
  })

  const getAuthImage = (source: AuthSource) => {
    if (source === AuthSource.Google) {
      return GoogleAuthIcon
    }
    if (source === AuthSource.Email) {
      return EmailAuthIcon
    }
    if (source === AuthSource.Mobile) {
      return MobileAuthIcon
    }
    return null
  }

  const getAuthTitle = (source: AuthSource) => {
    if (source === AuthSource.Google) {
      return t('谷歌验证')
    }
    if (source === AuthSource.Email) {
      return t('邮箱验证')
    }
    if (source === AuthSource.Mobile) {
      return t('手机验证')
    }
    return ''
  }

  const completedAuthNum = computed(() => {
    return Array.from(needSecurityAuthMap.value.values()).filter((item) => item.isCompleted).length
  })

  const submitAuth = () => {
    if (completedAuthNum.value < minVerifyNum.value) {
      UiUtils.showError(t('请完成所有验证'))
      return
    }
  }

  const authGoogle = async () => {
    if (googleCode.value.length !== 6) {
      UiUtils.showError(t('请输入6位谷歌验证码'))
      return false
    }
    if (loadingAuthGoogle.value) return false
    loadingAuthGoogle.value = true
    const res = await UCenterServices.safetyVerifyGoogle(
      Object.assign(
        {
          googleCode: googleCode.value,
          type: type.value!,
        },
        tempToken.value ? { token: tempToken.value } : {},
      ),
    )
    loadingAuthGoogle.value = false
    if (res.status !== ApiStatus.Success) {
      UiUtils.showError(res.msg)
      return false
    }
    const googleAuth = needSecurityAuthMap.value.get(AuthSource.Google)
    if (googleAuth) {
      googleAuth.isCompleted = true
    }
    lastVerifyResult.value = res.data
    tokenVerify.value = res.data.tokenVerify
    isAllAuthCompleted.value = completedAuthNum.value === minVerifyNum.value
    return true
  }

  const authEmail = async () => {
    if (emailCode.value.length !== 6) {
      UiUtils.showError(t('请输入6位邮箱验证码'))
      return false
    }
    if (loadingAuthEmail.value) return false
    loadingAuthEmail.value = true
    const res = await UCenterServices.safetyVerifyEmail(
      Object.assign(
        {
          emailCode: emailCode.value,
          type: type.value!,
        },
        tempToken.value ? { token: tempToken.value } : {},
      ),
    )
    loadingAuthEmail.value = false
    if (res.status !== ApiStatus.Success) {
      UiUtils.showError(res.msg)
      return false
    }
    const emailAuth = needSecurityAuthMap.value.get(AuthSource.Email)
    if (emailAuth) {
      emailAuth.isCompleted = true
    }
    tokenVerify.value = res.data.tokenVerify
    lastVerifyResult.value = res.data
    isAllAuthCompleted.value = completedAuthNum.value === minVerifyNum.value
    return true
  }

  const authMobile = async (phone?: SerializerPhone) => {
    if (mobileCode.value.length !== 6) {
      UiUtils.showError(t('请输入6位手机验证码'))
      return false
    }
    if (loadingAuthMobile.value) return false
    loadingAuthMobile.value = true
    const res = await UCenterServices.safetyVerifyMobile(
      Object.assign(
        {
          mobileCode: mobileCode.value,
          type: type.value!,
        },
        tempToken.value ? { token: tempToken.value } : {},
        phone ? { phone: JSON.stringify(phone) } : {},
      ),
    )
    loadingAuthMobile.value = false
    if (res.status !== ApiStatus.Success) {
      UiUtils.showError(res.msg)
      return false
    }
    const mobileAuth = needSecurityAuthMap.value.get(AuthSource.Mobile)
    if (mobileAuth) {
      mobileAuth.isCompleted = true
    }
    tokenVerify.value = res.data.tokenVerify
    lastVerifyResult.value = res.data
    isAllAuthCompleted.value = completedAuthNum.value === minVerifyNum.value
    return true
  }

  const setType = (_type: CommonConfirmSecurityAuthType) => {
    type.value = _type
  }

  const dispose = () => {
    currentAuthSource.value = undefined
    needSecurityAuthMap.value.clear()
    tokenVerify.value = ''
    googleCode.value = ''
    emailCode.value = ''
    mobileCode.value = ''
    isAllAuthCompleted.value = false
  }

  const handleEmailCountDown = () => {
    setTimeout(() => {
      if (emailCountDown.value > 0) {
        emailCountDown.value--
        handleEmailCountDown()
      } else {
        emailCountDown.value = -1
      }
    }, 1000)
  }

  const runEmailCountDown = () => {
    emailCountDown.value = 60
    handleEmailCountDown()
  }

  const sendVerifyEmailCode = async () => {
    if (emailCountDown.value > 0) return
    runEmailCountDown()
    const res = await UCenterServices.sendVerifyEmailCode(
      Object.assign(
        {
          type: type.value!,
        },
        tempToken.value ? { token: tempToken.value } : {},
      ),
    )

    if (res.status !== ApiStatus.Success) {
      UiUtils.showError(res.msg)
      return
    }
    UiUtils.showSuccess(t('验证码发送成功'))
  }

  const handleMobileCountDown = () => {
    setTimeout(() => {
      if (mobileCountDown.value > 0) {
        mobileCountDown.value--
        handleMobileCountDown()
      } else {
        mobileCountDown.value = -1
      }
    }, 1000)
  }

  const runMobileCountDown = () => {
    mobileCountDown.value = 60
    handleMobileCountDown()
  }

  const sendVerifyMobileCode = async (phone?: SerializerPhone) => {
    if (mobileCountDown.value > 0) return
    runMobileCountDown()
    const res = await UCenterServices.sendVerifyMobileCode(
      Object.assign(
        {
          type: type.value!,
        },
        tempToken.value ? { token: tempToken.value } : {},
        phone ? { phone: JSON.stringify(phone) } : {},
      ),
    )

    if (res.status !== ApiStatus.Success) {
      UiUtils.showError(res.msg)
      return
    }
    UiUtils.showSuccess(t('验证码发送成功'))
  }
  const close = () => {
    checkTradePwdVisible.value = false
  }

  return {
    showSecurityDialog,
    checkTradePwdVisible,
    close,
    needSecurityAuthMap,
    getAuthImage,
    getAuthTitle,
    completedAuthNum,
    submitAuth,
    currentAuthSource,
    setType,
    authGoogle,
    authEmail,
    authMobile,
    googleCode,
    emailCode,
    mobileCode,
    loadingAuthGoogle,
    loadingAuthEmail,
    loadingAuthMobile,
    isAllAuthCompleted,
    tokenVerify,
    dispose,
    mobileCountDown,
    emailCountDown,
    sendVerifyEmailCode,
    sendVerifyMobileCode,
    type,
    tempToken,
    lastVerifyResult,
    minVerifyNum,
  }
}
